import { ChangeEvent, useEffect, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Stack, TextField, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import * as Yup from 'yup';
import { companyStore } from 'stores/companyStore';
import type { NewCompany } from 'stores/companyStore';
import { PATHS } from 'routes/paths';
import { personalDataStore } from 'stores/personalDataStore';
import axios from 'axios';

const validationSchema = Yup.object<Record<keyof NewCompany, Yup.AnySchema>>({
  email: Yup.string().trim().email('Некорректный email').required('Укажите email'),
  inn: Yup.string()
    .trim()
    .required('Укажите ИНН')
    .matches(/^\d{10}$|^\d{12}$/, 'ИНН должен содержать 10 или 12 цифр'),
  name: Yup.string()
    .trim()
    .required('Укажите название компании')
    .max(200, 'Слишком длинное значение')
});

type FieldErrors = Partial<Record<keyof NewCompany, string>>;

const NewCompanyPage = () => {
  const navigate = useNavigate();
  const { newCompany, setNewCompanyField } = companyStore;
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [error, setError] = useState<string | null>(null);
  const { user } = personalDataStore;

  useEffect(() => {
    setNewCompanyField('email', user?.email ?? '');
  }, [user?.email, setNewCompanyField]);

  const validateField = async (field: keyof NewCompany) => {
    try {
      await validationSchema.validateAt(field, newCompany);
      setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        setFieldErrors((prev) => ({ ...prev, [field]: err.message }));
      }
    }
  };

  const handleChange = (field: keyof NewCompany) => (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setNewCompanyField(field, value);
    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async () => {
    setFieldErrors({});
    try {
      await validationSchema.validate(newCompany, { abortEarly: false });
      const company = await companyStore.createCompany();
      await personalDataStore.refreshUser();
      navigate(PATHS.hr.newVacancy, { state: { companyId: company.id } });
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const nextErrors: FieldErrors = {};
        err.inner.forEach((e) => {
          if (e.path && e.message) {
            const key = e.path as keyof NewCompany;
            if (!nextErrors[key]) nextErrors[key] = e.message;
          }
        });
        setFieldErrors(nextErrors);
      } else if (axios.isAxiosError(err)) {
        setError(err.response?.data?.message);
      } else {
        setError('Ошибка при создании компании');
      }
    }
  };

  return (
    <Stack
      justifyContent="center"
      alignItems="center"
      sx={{ height: '100%', overflow: 'auto', mr: -3, pr: 3, ml: -3, pl: 3 }}
      spacing={10}
    >
      <Card variant="outlined" sx={{ maxWidth: 712, width: '100%' }}>
        <CardContent sx={{ p: 6 }}>
          <Stack spacing={4}>
            <Box>
              <Typography
                component="h1"
                variant="h5"
                align="center"
                textAlign="center"
                sx={{ mb: 4 }}
              >
                Регистрация компании
              </Typography>
            </Box>
            <Typography color="text.secondary" variant="body2">
              Укажите ваши реальные данные, чтобы пройти верификацию
            </Typography>

            <Box sx={{ position: 'relative', pb: 3 }}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={newCompany.email}
                onChange={handleChange('email')}
                onBlur={() => validateField('email')}
                variant="outlined"
                autoComplete="email"
                error={Boolean(fieldErrors.email)}
                helperText={fieldErrors.email}
                sx={{ position: 'relative' }}
                FormHelperTextProps={{
                  sx: { position: 'absolute', left: 0, right: 0, top: '100%', mt: 0.5 }
                }}
                inputProps={{
                  'aria-label': 'Email'
                }}
              />
            </Box>
            <Box sx={{ position: 'relative', pb: 3 }}>
              <TextField
                fullWidth
                label="ИНН"
                value={newCompany.inn}
                onChange={handleChange('inn')}
                onBlur={() => validateField('inn')}
                variant="outlined"
                error={Boolean(fieldErrors.inn)}
                helperText={fieldErrors.inn}
                sx={{ position: 'relative' }}
                FormHelperTextProps={{
                  sx: { position: 'absolute', left: 0, right: 0, top: '100%', mt: 0.5 }
                }}
                inputProps={{
                  'aria-label': 'ИНН',
                  inputMode: 'numeric',
                  maxLength: 12
                }}
                placeholder="10 или 12 цифр"
              />
            </Box>
            <Box sx={{ position: 'relative', pb: 3 }}>
              <TextField
                fullWidth
                label="Название компании"
                value={newCompany.name}
                onChange={handleChange('name')}
                onBlur={() => validateField('name')}
                variant="outlined"
                error={Boolean(fieldErrors.name)}
                helperText={fieldErrors.name}
                sx={{ position: 'relative' }}
                FormHelperTextProps={{
                  sx: { position: 'absolute', left: 0, right: 0, top: '100%', mt: 0.5 }
                }}
                inputProps={{
                  'aria-label': 'Название компании'
                }}
              />
            </Box>
          </Stack>
          {error && <Alert severity="error">{error}</Alert>}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              pt: 12
            }}
          >
            <Button
              size="large"
              variant="contained"
              onClick={handleSubmit}
              aria-label="Продолжить"
              sx={{ width: '250px' }}
            >
              Продолжить
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Stack>
  );
};

export default observer(NewCompanyPage);
